import { HttpBackend, HttpClient, HttpContextToken, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { canReplayAfterRefresh, markNotReplayedAfterRefresh } from '@lite-workspace/utils';
import { Observable, catchError, finalize, from, shareReplay, switchMap, throwError } from 'rxjs';
import { AuthState, clearAuth, readAuthState, redirectToIdentity, writeAuthState } from './auth';
import { invalidateBffSession, isBffMode, probeBffSession, redirectToBffLogin } from './bff';

export const RETRIED = new HttpContextToken<boolean>(() => false);
// A no-login page's own read: its 401 is that page's error, never a reason to send the guest to sign in.
export const NO_SESSION_REDIRECT = new HttpContextToken<boolean>(() => false);

// Dedupes concurrent 401s so only one PUT /api/auth/tokens is in-flight at a time.
let refreshing$: Observable<AuthState> | null = null;

function getOrStartRefresh(bypassHttp: HttpClient): Observable<AuthState> {
  if (!refreshing$) {
    const state = readAuthState();
    refreshing$ = bypassHttp
      .put<AuthState>('/api/auth/tokens', {
        accessToken: state?.accessToken,
        refreshToken: state?.refreshToken,
      })
      .pipe(
        finalize(() => {
          refreshing$ = null;
        }),
        shareReplay(1),
      );
  }

  return refreshing$;
}

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  // Refresh PUT uses a bare HttpClient (HttpBackend) to bypass this interceptor and avoid re-entry.
  const bypassHttp = new HttpClient(inject(HttpBackend));

  return next(req).pipe(
    catchError((err: HttpErrorResponse) => {
      if (err.status !== 401 || req.context.get(NO_SESSION_REDIRECT)) return throwError(() => err);

      // Фоновый 401 не значит мёртвую сессию — logout только если сервер это подтвердит.
      if (isBffMode()) {
        return from(probeBffSession()).pipe(
          switchMap(verdict => {
            if (verdict === 'dead') {
              invalidateBffSession();
              redirectToBffLogin(router.url);
            }

            return throwError(() => err);
          }),
        );
      }

      if (req.url.includes('/api/auth/tokens') || req.context.get(RETRIED)) {
        clearAuth();
        redirectToIdentity(router.url);
        return throwError(() => err);
      }

      return getOrStartRefresh(bypassHttp).pipe(
        // A failed refresh ends the session; errors of the retried request must NOT — a 403/500
        // after a successful refresh is that request's problem, and logging out on it throws the
        // photographer to login over an ordinary server error.
        catchError(() => {
          clearAuth();
          redirectToIdentity(router.url);
          return throwError(() => err);
        }),
        switchMap(newState => {
          writeAuthState({ accessToken: newState.accessToken, refreshToken: newState.refreshToken });
          if (!canReplayAfterRefresh(req.method)) return throwError(() => markNotReplayedAfterRefresh(err));
          const retried = req.clone({
            setHeaders: { Authorization: `Bearer ${newState.accessToken}` },
            context: req.context.set(RETRIED, true),
          });
          return next(retried).pipe(
            catchError((retryErr: HttpErrorResponse) => {
              // Only a second 401 means the fresh token is worthless too — that is a dead session.
              if (retryErr.status === 401) {
                clearAuth();
                redirectToIdentity(router.url);
              }

              return throwError(() => retryErr);
            }),
          );
        }),
      );
    }),
  );
};
